import type { BotAvatarSet } from "@orbin/contracts";
import { BOT_AVATAR_COLORS, DEFAULT_BOT_AVATAR_COLOR, resolvePersonaColorDef } from "@orbin/core";
import { memo, useMemo } from "react";
import { cn } from "./lib/utils.js";
import "./styles.css";

export { BOT_AVATAR_COLORS, DEFAULT_BOT_AVATAR_COLOR, resolvePersonaColorDef };

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * The identity color a stored bot color value stands for. Older values carried a shape or an
 * uploaded picture beside the color; only the color is read now.
 */
export function botColorValue(rawColor: string): string {
  const color = rawColor.split("::shape_")[0] ?? "";
  return HEX.test(color) ? color : DEFAULT_BOT_AVATAR_COLOR;
}

/** The identity color a teammate wears, from its stored color and id. */
export function botIdentityColor(rawColor: string, identity = ""): string {
  const plain = rawColor.startsWith("data:image/") ? undefined : rawColor.split("::shape_")[0];
  return resolvePersonaColorDef(identity || plain || "agent", plain).hex;
}

/** A teammate's identity color as a small dot; the name beside it says who it is. */
export const BotDot = memo(function BotDot({
  color,
  identity,
  size = 6,
  className,
}: {
  color: string;
  identity?: string;
  size?: number;
  className?: string;
}) {
  const hex = useMemo(() => botIdentityColor(color, identity), [color, identity]);
  return (
    <span
      aria-hidden="true"
      data-bot-dot=""
      className={cn("inline-block shrink-0 rounded-full", className)}
      style={{ width: size, height: size, backgroundColor: hex }}
    />
  );
});

/** The identity colors a teammate can be given, as a row of choices. */
export interface BotDotMember {
  botId?: string;
  name?: string;
  color: string;
  avatarSet?: BotAvatarSet | undefined;
  status?: string;
}

/** A group's members as a short run of identity dots. */
export function BotDotStack({
  members,
  size = 6,
  max = 4,
  className,
}: {
  members: readonly BotDotMember[];
  size?: number;
  max?: number;
  className?: string;
}) {
  return (
    <span aria-hidden="true" className={cn("inline-flex shrink-0 items-center gap-0.5", className)}>
      {members.slice(0, max).map((member, index) => (
        <BotDot
          key={member.botId ?? index}
          color={member.color}
          identity={member.botId}
          size={size}
        />
      ))}
    </span>
  );
}

/** The identity colors a teammate can be given, as a row of choices. */
export function BotColorPicker({
  value,
  onChange,
  label,
  disabled,
}: {
  value: string;
  onChange: (color: string) => void;
  /** Accessible name for each swatch, given its hex. */
  label: (color: string) => string;
  disabled?: boolean;
}) {
  const current = botColorValue(value).toLowerCase();
  return (
    <div className="grid grid-cols-6 place-items-center gap-2">
      {BOT_AVATAR_COLORS.map((color) => {
        const selected = current === color.toLowerCase();
        return (
          <button
            key={color}
            type="button"
            disabled={disabled}
            onClick={() => onChange(color)}
            aria-label={label(color)}
            aria-pressed={selected}
            className={cn(
              "size-6 rounded-full border transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-ring active:scale-95",
              selected
                ? "scale-105 border-transparent ring-2 ring-foreground ring-offset-2 ring-offset-popover"
                : "border-border",
            )}
            style={{ backgroundColor: color }}
          />
        );
      })}
    </div>
  );
}

export function Wordmark({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div className="flex h-11 w-11 items-center justify-center gap-1.5 rounded-full bg-card">
        <span className="h-4 w-[7px] rounded-full bg-primary" />
        <span className="h-4 w-[7px] rounded-full bg-primary" />
      </div>
      <span className="font-[Aeonik,ui-sans-serif] text-[28px] tracking-tight text-foreground">
        Preá
      </span>
    </div>
  );
}

